import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Paper,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Button,
  Typography,
  Link,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import { red, green } from '@material-ui/core/colors';
import Web3Service from '../../services/Web3Service.jsx';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: '0 5px',
    },
  },
  depositContext: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  fixedHeight: {
    height: 240,
  },
  card: {
    height: 250,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  avatar: {
    backgroundColor: red[500],
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

function Contract() {
  const classes = useStyles();
  const fixedHeightPaper = clsx(classes.fixedHeight, classes.card);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [publicAddress, setPublicAddress] = useState(null);
  const [owner, setOwner] = useState(null);
  const [balance, setBalance] = useState(0);
  const [feesLoading, setFeesLoading] = useState(false);
  const [fees, setFees] = useState(0);
  const [lastUpdate, setlastUpdate] = useState(0);
  const [lastFeesUpdate, setlastFeesUpdate] = useState(0);
  // const [success, setSuccess] = React.useState(false);

  const connect = async () => {
    try {
      const address = await Web3Service.connect();
      setPublicAddress(address);
    } catch (err) {
      throw new Error(err);
    }
  };

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const data = await Web3Service.contractBalance();
      setBalance(data);
      const today = new Date();
      const date = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      const dateTime = `${date} at ${time}`;
      setlastUpdate(dateTime);
    } catch (err) {
      console.error('Error fetching balance:', err);
      setBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchFees = async () => {
    setFeesLoading(true);
    try {
      const tmp = await Web3Service.estimateFees();
      setFees(tmp);
      const today = new Date();
      const date = `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      const dateTime = `${date} at ${time}`;
      setlastFeesUpdate(dateTime);
    } catch (err) {
      console.error('Error fetching fees:', err);
      setFees(0);
    } finally {
      setFeesLoading(false);
    }
  };

  const test = async () => {
    try {
      const tmp = await Web3Service.fetchOwner();
      setOwner(tmp);
    } catch (err) {
      console.error('Error fetching owner:', err);
      setOwner(null);
    }
  };

  const claimFees = async () => {
    setFeesLoading(true);
    try {
      const transactionHash = await Web3Service.withdrawFees();
      console.log('Fees withdrawn successfully. Transaction hash:', transactionHash);
      // Refresh fees balance after successful withdrawal
      await fetchFees();
    } catch (err) {
      console.error('Error withdrawing fees:', err);
      alert(`Failed to withdraw fees: ${err.message || 'Unknown error'}`);
    } finally {
      setFeesLoading(false);
    }
  };

  const url = (process.env.INFURA_NETWORK === 'polygon-amoy') ? 'amoy.polygonscan.com' : 'polygonscan.com';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper className={classes.paper} variant="outlined">
          <div className={classes.root}>
            <Button variant="contained" display="inline" onClick={connect} disabled={(publicAddress !== null)}>
              {(!publicAddress) ? 'Connect METAMASK' : 'Connected'}
            </Button>
            {
              (publicAddress)
              && <Typography display="inline">
                  Wallet: {publicAddress}
                </Typography>
            }
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={12} lg={6}>
        <Card className={fixedHeightPaper} variant="outlined">
          <CardHeader
            avatar={
              <Avatar aria-label="recipe" className={classes.avatar}>
                C
              </Avatar>
            }
            title="Contract"
            subheader="Contract information"
          />
          <CardContent>
            <Typography color="textSecondary" className={classes.depositContext}>
              Address:{' '}
              <Link color="primary" href={`https://${url}/address/${process.env.CONTRACT_ADDRESS}`} target="_blank">
                {process.env.CONTRACT_ADDRESS}
              </Link>
            </Typography>
            <Typography color="textSecondary" className={classes.depositContext}>
              Owner:{' '}
              {
                (owner)
                && <Link color="primary" href={`https://${url}/address/${owner}`} target="_blank">
                  {owner}
                </Link>
              }
            </Typography>
          </CardContent>
          <CardActions>
            <Button size="small" variant="outlined" onClick={test}>Update</Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card className={fixedHeightPaper} variant="outlined">
          <CardHeader
            avatar={
              <Avatar aria-label="recipe" className={classes.avatar}>
                F
              </Avatar>
            }
            title="Fees"
            subheader="Current accumulated fees"
          />
          <CardContent>
            <Typography component="p" variant="h4">
              {fees} ETH
            </Typography>
            <Typography color="textSecondary" className={classes.depositContext}>
              updated: {lastFeesUpdate}
            </Typography>
          </CardContent>
          <CardActions>
            <div className={classes.wrapper}>
              <Button size="small" variant="outlined" onClick={fetchFees} disabled={(publicAddress === null) || (feesLoading)}>Update</Button>
              {feesLoading && <CircularProgress size={20} className={classes.buttonProgress} />}
            </div>
            <Button size="small" variant="outlined" onClick={claimFees}>Claim</Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card className={fixedHeightPaper} variant="outlined">
          <CardHeader
            avatar={
              <Avatar aria-label="recipe" className={classes.avatar}>
                B
              </Avatar>
            }
            title="Balance"
            subheader="Current contract balance"
          />
          <CardContent>
            <Typography component="p" variant="h4">
              {balance} ETH
            </Typography>
            <Typography color="textSecondary" className={classes.depositContext}>
              updated: {lastUpdate}
            </Typography>
          </CardContent>
          <CardActions>
            <div className={classes.wrapper}>
              <Button size="small" variant="outlined" disabled={balanceLoading} onClick={fetchBalance}>
                Update
              </Button>
              {balanceLoading && <CircularProgress size={20} className={classes.buttonProgress} />}
            </div>
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );
}

export default Contract;
